import { useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch, thumbUrls, uploadSchoolLogo, uploadUrl } from '../config/api';

export default function SchoolLogoField({
  schoolId,
  schoolName,
  value,
  onChange,
  token,
  disabled = false,
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState('');
  const [previewIndex, setPreviewIndex] = useState(0);

  const previewCandidates = useMemo(() => {
    const full = uploadUrl(value);
    const thumbs = thumbUrls(value);
    return [...thumbs, full].filter(Boolean);
  }, [value]);

  const previewSrc = previewCandidates[previewIndex] || null;

  useEffect(() => {
    setPreviewIndex(0);
    setError('');
  }, [value]);

  const persistLogo = async (nextPath) => {
    if (!schoolId) {
      onChange?.(nextPath);
      return;
    }
    await apiFetch(`/schools/${schoolId}`, {
      method: 'PUT',
      body: JSON.stringify({ logo_relative_path: nextPath }),
    }, token);
    onChange?.(nextPath);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('school-logo-updated', {
        detail: { schoolId, logo_relative_path: nextPath || null },
      }));
    }
  };

  const handlePick = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setError('');
    setUploading(true);
    try {
      const res = await uploadSchoolLogo(file, token);
      await persistLogo(res.logo_relative_path);
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    setError('');
    setRemoving(true);
    try {
      await persistLogo(null);
    } catch (err) {
      setError(err.message || 'Could not remove logo');
    } finally {
      setRemoving(false);
    }
  };

  const busy = uploading || removing || disabled;

  return (
    <div className="ssp-logo-field">
      <div className="ssp-logo-preview" aria-label={`${schoolName || 'School'} logo`}>
        {previewSrc ? (
          <img
            src={previewSrc}
            alt=""
            onError={() => setPreviewIndex((current) => current + 1)}
          />
        ) : (
          <span className="ssp-logo-placeholder">No logo</span>
        )}
      </div>

      <div className="ssp-logo-actions">
        <div className="ssp-logo-buttons">
          <button
            type="button"
            className="sa-btn sa-btn-secondary"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
          >
            {uploading ? 'Uploading...' : value ? 'Replace Logo' : 'Upload Logo'}
          </button>
          {value ? (
            <button
              type="button"
              className="sa-btn sa-btn-secondary"
              onClick={handleRemove}
              disabled={busy}
            >
              {removing ? 'Removing...' : 'Remove'}
            </button>
          ) : null}
        </div>
        <div className="ssp-logo-hint">
          PNG, JPEG, WebP, or GIF up to 10MB. Square or transparent PNG works best.
        </div>
        {error ? <div className="sa-error" style={{ marginTop: 4 }}>{error}</div> : null}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={handlePick}
        style={{ display: 'none' }}
      />
    </div>
  );
}
